// Cadastro de Backlog Inicial (issue #68, ADR-0019): o saldo que já existia
// num Cliente antes do sistema entrar em operação, sem Lote de origem.
// Cadastrar de novo substitui as quantidades anteriores desse Cliente. É a
// única forma de o Backlog Inicial mudar (ADR-0027): nunca negativo, nunca
// abaixo do que já saiu dele.

import { baixadoBacklogInicial, perdidoBacklogInicial } from "./backlog.ts";
import type { Id } from "./ids.ts";
import type { Snapshot } from "./snapshot.ts";

export class ErroBacklogInicial extends Error {}

export interface LinhaBacklogInicial {
  tipoId: Id;
  quantidade: number;
}

/**
 * Substitui o Backlog Inicial de um Cliente pelas linhas informadas — uma por
 * Tipo de Peça, sem Motivo (ADR-0019). Linhas com quantidade zero não geram
 * registro; um Tipo que ficou de fora vale zero.
 *
 * Toda quantidade que **muda** fica em zero ou mais, e nunca abaixo do que já
 * saiu desse Backlog Inicial (carona + Perda) — senão o Histórico mostraria
 * peças saindo de um saldo que não existia (ADR-0027). O que não muda passa
 * como está: um negativo antigo não trava editar os outros Tipos do Cliente.
 */
export function definirBacklogInicial(
  snapshot: Snapshot,
  clienteId: Id,
  linhas: readonly LinhaBacklogInicial[],
): Snapshot {
  if (!snapshot.clientes.some((c) => c.id === clienteId)) {
    throw new ErroBacklogInicial("Cliente não encontrado.");
  }
  const vistos = new Set<Id>();
  const efetivo = new Map<Id, number>();
  for (const l of linhas) {
    if (!snapshot.tiposPeca.some((t) => t.id === l.tipoId)) {
      throw new ErroBacklogInicial("Tipo de peça não encontrado.");
    }
    if (vistos.has(l.tipoId)) {
      throw new ErroBacklogInicial("O mesmo tipo de peça apareceu duas vezes.");
    }
    vistos.add(l.tipoId);
    if (!Number.isInteger(l.quantidade)) {
      throw new ErroBacklogInicial("Quantidade inválida.");
    }
    efetivo.set(l.tipoId, l.quantidade);
  }

  const atual = new Map(
    snapshot.backlogInicial.filter((b) => b.clienteId === clienteId).map((b) => [b.tipoId, b.quantidade]),
  );
  for (const tipoId of new Set([...atual.keys(), ...efetivo.keys()])) {
    const nova = efetivo.get(tipoId) ?? 0;
    if (nova === (atual.get(tipoId) ?? 0)) continue;
    const nome = snapshot.tiposPeca.find((t) => t.id === tipoId)?.nome ?? "peça";
    if (nova < 0) throw new ErroBacklogInicial(`${nome}: a quantidade não pode ser negativa.`);
    const saiu =
      baixadoBacklogInicial(snapshot, clienteId, tipoId) + perdidoBacklogInicial(snapshot, clienteId, tipoId);
    if (nova < saiu) {
      throw new ErroBacklogInicial(
        `${nome}: já saíram ${saiu} peça(s) deste Backlog Inicial — o mínimo é ${saiu}.`,
      );
    }
  }

  const outras = snapshot.backlogInicial.filter((b) => b.clienteId !== clienteId);
  const novas = linhas
    .filter((l) => l.quantidade !== 0)
    .map((l) => ({ clienteId, tipoId: l.tipoId, quantidade: l.quantidade }));

  return { ...snapshot, backlogInicial: [...outras, ...novas] };
}
